import { queryOptions, useQueries, useQuery, QueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { ReactNode } from 'react'

export class Query<Key extends readonly unknown[], FlatKey extends unknown[], Ret> {
	private _fetch?: (...key: FlatKey) => Promise<Ret>

	constructor(
		private _flatKey: (...t: Key) => FlatKey,
		private _useFetch: () => (...key: FlatKey) => Promise<Ret>
	) {}

	flatKey(...t: Key) {
		return this._flatKey(...t)
	}

	useSetUp() {
		this._fetch = this._useFetch()
	}

	private useQueryOptions() {
		const fetch = this._useFetch()
		return (t: Key) => queryOptions({
			retry: false, // HTTP client does the retries.
			staleTime: Infinity,
			queryKey: this._flatKey(...t),
			queryFn: ({queryKey}: {queryKey: FlatKey}) => fetch(...queryKey),
		})
	}

	use(...t: Key) {
		const options = this.useQueryOptions()
		return useQuery(options(t))
	}

	useSuspense(...t: Key) {
		const options = this.useQueryOptions()
		return useSuspenseQuery(options(t))
	}

	useMany(ts: Key[]) {
		const options = this.useQueryOptions()
		return useQueries({queries: ts.map(t => options(t))})
	}

	noUse() {
		this.useQueryOptions()
		return useQuery({
			staleTime: Infinity,
			queryKey: ['nouse'],
			queryFn: () => null,
		})
	}

	async get(qc: QueryClient, t: Key) {
		if (!this._fetch) {
			throw new Error('Use QuerySetup first!')
		}
		return {data: await qc.ensureQueryData({
			staleTime: Infinity,
			queryKey: this._flatKey(...t),
			queryFn: ({queryKey}: {queryKey: FlatKey}) => this._fetch!(...queryKey),
		})}
	}

	setData(qc: QueryClient, t: Key, data: Ret) {
		qc.setQueryData<Ret>(this.flatKey(...t), data)
	}

	async patchData(qc: QueryClient, t: Key, updater: (oldData: Ret) => Ret) {
		const queryKey = this.flatKey(...t)

		await this.get(qc, t)
		const state = qc.getQueryState<Ret>(queryKey)
		if (!state || !state.data) {
			console.warn('Can`t patch a query data that`s not there.')
			qc.invalidateQueries({queryKey})
			return
		}
		qc.setQueryData<Ret>(queryKey, updater(state.data), {
			updatedAt: state.dataUpdatedAt
		})
	}

	async invalidate(qc: QueryClient, t: Key) {
		await qc.invalidateQueries({queryKey: this.flatKey(...t)})
	}
}

export function QuerySetup({queries, children}: {
	queries: Query<any, any, any>[]
	children: ReactNode
}) {
	for (const query of queries) {
		query.useSetUp()
	}

	return children
}
